import React, { useState, useContext, useEffect } from 'react';
import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import AuthContext from '../../contexts/AuthContext';
import { PhotoIcon, VideoCameraIcon, XMarkIcon as XIcon } from '@heroicons/react/24/outline';
import { uploadToCloudinary } from '../../utils/cloudinary';
import { sanitizeText } from '../../utils/sanitize';
import { extractHashtags, processHashtags, getTrendingHashtags } from '../../services/hashtagService';
import { useTranslation } from 'react-i18next';
import { notifyFollowersAboutNewPost } from '../../services/postNotificationService';

const CreatePostForm: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const [content, setContent] = useState('');
  const [media, setMedia] = useState<File | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [userDisplayName, setUserDisplayName] = useState<string | null>(null);
  const [userPhotoURL, setUserPhotoURL] = useState<string | null>(null);
  const [trendingHashtags, setTrendingHashtags] = useState<Array<{ id: string, tag: string }>>([]);
  const [showHashtagSuggestions, setShowHashtagSuggestions] = useState(false);
  const [_isTypingHashtag, setIsTypingHashtag] = useState(false);
  const [_currentHashtag, setCurrentHashtag] = useState('');
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const { t: _t } = useTranslation();

  // Cargar la información más reciente del usuario al montar el componente
  useEffect(() => {
    const loadUserData = async () => {
      if (!currentUser) return;

      try {
        // Obtener los datos más recientes del usuario desde Firestore
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserDisplayName(userData.displayName || currentUser.displayName || '');
          setUserPhotoURL(userData.photoURL || currentUser.photoURL || '');
        } else {
          // Si no hay documento de usuario, usar los datos de Auth
          setUserDisplayName(currentUser.displayName || '');
          setUserPhotoURL(currentUser.photoURL || '');
        }
      } catch (error) {
        console.error('Error al cargar datos del usuario:', error);
        // En caso de error, usar los datos de Auth como respaldo
        setUserDisplayName(currentUser.displayName || '');
        setUserPhotoURL(currentUser.photoURL || '');
      }
    };

    loadUserData();
  }, [currentUser]);

  // Cargar hashtags en tendencia al montar el componente
  useEffect(() => {
    const loadTrendingHashtags = async () => {
      try {
        const hashtags = await getTrendingHashtags(10);
        setTrendingHashtags(hashtags.map(tag => ({
          id: tag.id,
          tag: tag.tag
        })));
      } catch (error) {
        console.error('Error al cargar hashtags en tendencia:', error);
      }
    };

    loadTrendingHashtags();
  }, []);

  const handleMediaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];

    if (!file) return;

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('El archivo es demasiado grande. El tamaño máximo es 10MB.');
      return;
    }

    // Determine file type
    const fileType = file.type.startsWith('image/') ? 'image' :
      file.type.startsWith('video/') ? 'video' : null;

    if (!fileType) {
      setError('Tipo de archivo no soportado. Solo se permiten imágenes y videos.');
      return;
    }

    setError('');
    setMedia(file);
    setMediaType(fileType);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setMediaPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const removeMedia = () => {
    setMedia(null);
    setMediaPreview(null);
    setMediaType(null);
  };

  const uploadMedia = async (): Promise<string | null> => {
    if (!media) return null;

    setIsUploading(true);
    setError('');
    try {
      // Usar Cloudinary para subir medios
      const mediaUrl = await uploadToCloudinary(media);
      return mediaUrl;
    } catch (uploadError: any) {
      console.error('Error uploading to Cloudinary:', uploadError);

      // Mostrar mensaje de error más descriptivo
      if (uploadError?.message) {
        // Si el error viene de nuestra función uploadToCloudinary con mensaje personalizado
        setError(uploadError.message);
      } else {
        // Mensaje genérico si no hay detalles
        setError('Error al subir el archivo. Verifica tu conexión e inténtalo de nuevo.');
      }

      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser || (!content.trim() && !media)) {
      setError('Escribe algo o añade una imagen/video para publicar.');
      return;
    }

    setError('');

    try {
      let mediaUrl = null;
      if (media) {
        setIsUploading(true);
        mediaUrl = await uploadMedia();
        if (!mediaUrl) {
          setIsUploading(false);
          return;
        }
      }

      // Sanitizar el contenido antes de guardarlo
      const sanitizedContent = sanitizeText(content.trim());

      // Extraer hashtags del contenido
      const hashtags = extractHashtags(sanitizedContent);

      // Crear el documento del post
      const postRef = await addDoc(collection(db, 'posts'), {
        userId: currentUser.uid,
        userName: userDisplayName || currentUser.displayName || 'Usuario',
        userPhotoURL: userPhotoURL || currentUser.photoURL || '/default-avatar.png',
        content: sanitizedContent,
        hashtags: hashtags, // Guardar los hashtags en el documento
        likes: 0,
        comments: 0,
        createdAt: serverTimestamp(),
        hasMedia: !!mediaUrl
      });

      // Procesar hashtags con el ID del post
      if (hashtags.length > 0) {
        await processHashtags(hashtags, postRef.id);
      }

      if (mediaUrl) {
        if (mediaType === 'image') {
          await updateDoc(postRef, { imageUrl: mediaUrl });
        } else if (mediaType === 'video') {
          await updateDoc(postRef, { videoUrl: mediaUrl });
        }
      }

      // Notificar a los seguidores sobre el nuevo post
      try {
        await notifyFollowersAboutNewPost(
          currentUser.uid,
          postRef.id,
          userDisplayName || currentUser.displayName || 'Usuario',
          userPhotoURL || currentUser.photoURL || '/default-avatar.png'
        );
        // Toast de notificación exitosa
        const notifEvent = new CustomEvent('showToast', {
          detail: {
            title: 'Seguidores notificados',
            message: 'Tus seguidores han sido notificados del nuevo post.',
            type: 'info',
          }
        });
        window.dispatchEvent(notifEvent);
      } catch (notifError) {
        // Toast de error de notificación
        const notifEvent = new CustomEvent('showToast', {
          detail: {
            title: 'Error al notificar',
            message: 'El post fue creado, pero no se pudo notificar a los seguidores.',
            type: 'error',
          }
        });
        window.dispatchEvent(notifEvent);
      }

      // Mostrar toast de éxito
      const event = new CustomEvent('showToast', {
        detail: {
          title: 'Publicación creada',
          message: 'Tu publicación se ha creado correctamente',
          type: 'success'
        }
      });
      window.dispatchEvent(event);

      setContent('');
      setMedia(null);
      setMediaPreview(null);
      setMediaType(null);
    } catch (error) {
      console.error('Error creating post:', error);
      setError('Error al crear la publicación. Inténtalo de nuevo.');
    } finally {
      setIsUploading(false);
    }
  };

  // Detectar cuando el usuario está escribiendo un hashtag
  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setContent(newContent);

    // Obtener la posición del cursor
    const cursorPosition = e.target.selectionStart;

    // Obtener el texto hasta la posición del cursor
    const textUntilCursor = newContent.substring(0, cursorPosition);

    // Comprobar si estamos escribiendo un hashtag
    const hashtagMatch = textUntilCursor.match(/(^|\s)(#[\w]*)$/); // Coincide con # seguido de caracteres alfanuméricos al final del texto

    if (hashtagMatch) {
      const currentTag = hashtagMatch[2]; // El hashtag actual que se está escribiendo
      setIsTypingHashtag(true);
      setCurrentHashtag(currentTag);
      setShowHashtagSuggestions(true);
    } else {
      setIsTypingHashtag(false);
      setShowHashtagSuggestions(false);
    }
  };

  // Insertar un hashtag sugerido en el texto
  const insertHashtag = (hashtag: string) => {
    if (!textareaRef.current) return;

    const cursorPosition = textareaRef.current.selectionStart;
    const textBeforeCursor = content.substring(0, cursorPosition);

    // Encontrar dónde comienza el hashtag actual
    const hashtagStartMatch = textBeforeCursor.match(/(^|\s)(#[\w]*)$/);

    if (hashtagStartMatch) {
      const matchStart = hashtagStartMatch.index! + hashtagStartMatch[1].length;
      const beforeHashtag = content.substring(0, matchStart);
      const afterCursor = content.substring(cursorPosition);

      // Reemplazar el hashtag parcial con el hashtag completo
      const newContent = beforeHashtag + hashtag + ' ' + afterCursor;
      setContent(newContent);

      // Mover el cursor después del hashtag insertado
      setTimeout(() => {
        if (textareaRef.current) {
          const newPosition = matchStart + hashtag.length + 1; // +1 por el espacio
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newPosition, newPosition);
        }
      }, 0);
    }

    setShowHashtagSuggestions(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md" role="alert">
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      <div className="flex items-start space-x-3">
        <img
          src={userPhotoURL || '/default-avatar.png'}
          alt={userDisplayName || 'Usuario'}
          className="h-10 w-10 rounded-full object-cover"
        />
        <div className="flex-grow">
          <textarea
            value={content}
            onChange={handleContentChange}
            placeholder="¿Qué está pasando?"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-domired-500 focus:border-domired-500 dark:bg-gray-700"
            rows={3}
            required
            disabled={isUploading}
            ref={textareaRef}
          />

          {mediaPreview && (
            <div className="relative mt-2 rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={removeMedia}
                className="absolute top-2 right-2 bg-gray-800 bg-opacity-70 rounded-full p-1 text-white"
                aria-label="Remove media"
              >
                <XIcon className="h-4 w-4" />
              </button>
              {mediaType === 'image' ? (
                <img
                  src={mediaPreview}
                  alt="Media preview"
                  className="w-full h-auto max-h-60 object-contain"
                />
              ) : (
                <video
                  src={mediaPreview}
                  controls
                  className="w-full h-auto max-h-60"
                />
              )}
            </div>
          )}

          <div className="flex items-center justify-between mt-3">
            <div className="flex space-x-2">
              <label className="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-domired-600 dark:hover:text-domired-400">
                <PhotoIcon className="h-5 w-5" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleMediaChange}
                  className="hidden"
                  disabled={isUploading || !!media}
                />
              </label>
              <label className="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-domired-600 dark:hover:text-domired-400">
                <VideoCameraIcon className="h-5 w-5" />
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleMediaChange}
                  className="hidden"
                  disabled={isUploading || !!media}
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={isUploading || (!content.trim() && !media)}
              className="px-4 py-1.5 bg-domired-600 text-white font-medium rounded-full hover:bg-domired-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-domired-500 disabled:opacity-50"
            >
              {isUploading ? 'Publicando...' : 'Publicar'}
            </button>
          </div>
        </div>
      </div>

      {showHashtagSuggestions && (
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-md p-4 mt-2">
          <h4 className="text-lg font-bold mb-2">Hashtags sugeridos</h4>
          <ul>
            {trendingHashtags.map(hashtag => (
              <li key={hashtag.id} className="py-1">
                <button
                  type="button"
                  onClick={() => insertHashtag(hashtag.tag)}
                  className="text-domired-600 hover:text-domired-700"
                >
                  {hashtag.tag}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  );
};

export default CreatePostForm;
